import {Show, Text, TextSize, ThemeColorPath} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {EMPTY_PLACEHOLDER, suffixTestId} from 'shared/web';

import {LevelFill} from '../LevelInput/LevelFill';
import {LevelValueProps} from '../LevelInput/LevelValueProps';
import {formatPercent} from '../LevelInput/utils/formatPercent';
import {getLevelBandColor} from '../LevelInput/utils/getLevelBandColor';
import {resolveLevel} from '../LevelInput/utils/resolveLevel';

/**
 * How the glyph is coloured. `banded` repeats the band colours of the `BatteryCharge`
 * input; `neutral` paints the whole glyph in the secondary text colour, for rows
 * that have to read as text — an entity header parameter, for example.
 */
type BatteryChargeValueTone = 'banded' | 'neutral';

export interface BatteryChargeValueProps extends LevelValueProps {
  /**
   * Size of the percentage readout. The default `small` (20px line box) is the reviewed
   * design; `xSmall` (16px line box) is for callers that must fit the glyph into the line
   * box of an `xSmall` text row, such as an `Attributes` row.
   */
  readoutSize?: Extract<TextSize, 'small' | 'xSmall'>;
  /** Defaults to `banded`. */
  tone?: BatteryChargeValueTone;
}

const DEFAULT_READOUT_SIZE = 'small';
const DEFAULT_TONE = 'banded';
/** Fill colour of the `neutral` tone — the colour the surrounding header text is drawn in. */
const NEUTRAL_FILL_COLOR: ThemeColorPath = 'text.secondary';

const rootVariants = cva('inline-flex items-center gap-1 align-middle');
const glyphVariants = cva('inline-flex flex-none items-center gap-px');
const bodyVariants = cva(
  'relative box-border h-[calc(var(--eag-space-step,0.25)*3rem)] w-[calc(var(--eag-space-step,0.25)*7rem)] flex-none rounded-[var(--radius-small)] border border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)]',
  {
    variants: {
      isRecorded: {
        true: 'border-solid',
        false: 'border-dashed',
      },
    },
  }
);
const innerVariants = cva('absolute inset-[2px] overflow-hidden rounded-[var(--radius-x-small)]');
const terminalVariants = cva(
  'h-[6px] w-[3px] flex-none rounded-[var(--radius-x-small)] bg-[var(--color-palettes-neutral-50-100)]'
);

/** The glyph is decorative — the readout carries the value for assistive technology. */
export function BatteryChargeValue(props: BatteryChargeValueProps) {
  const {fraction, percent, isRecorded} = resolveLevel(props.value, props.min, props.max);
  const tone = props.tone ?? DEFAULT_TONE;

  return (
    <div className={cn(rootVariants())} data-testid={suffixTestId('batteryChargeValue', props)}>
      <div className={cn(glyphVariants())} aria-hidden>
        <div className={cn(bodyVariants({isRecorded}))} data-testid={suffixTestId('bar', props)}>
          <div className={cn(innerVariants())}>
            <Show when={isRecorded}>
              <LevelFill
                $fraction={fraction ?? 0}
                $bandColor={tone === 'neutral' ? NEUTRAL_FILL_COLOR : getLevelBandColor(percent)}
                $radius="xSmall"
              />
            </Show>
          </div>
        </div>
        <div className={cn(terminalVariants())} />
      </div>
      <Text
        size={props.readoutSize ?? DEFAULT_READOUT_SIZE}
        color="secondary"
        noWrap
        data-testid={suffixTestId('value', props)}
      >
        {isRecorded ? formatPercent(percent) : EMPTY_PLACEHOLDER}
      </Text>
    </div>
  );
}
